import React, { useState, useEffect } from "react";
import { SetupDataPageTemplate } from "../../../components/setup-pages/SetupDataPageTemplate";
import { Field, Form } from "react-jsonschema-form-validation";
import { setupData } from "../../../configs/routes";
import {
	saveYourDetails,
	saveUserPid,
	getUserSettings,
	saveYourControl,
} from "../../../services/userService";
import { getList } from "../../../services/dashApiService";
import { pathRoot } from "../YourDetails";
import { Select } from "../../../components/common/Select";

export function Page2({ userPid, yourDetails, onSaveFailure }) {
	const headerDetails = {
		title: "Your Details",
		currentPage: 2,
		pageCount: 2,
	};

	const formFieldsDefault = {
		heightFeet: yourDetails.height.feet ?? null,
		heightInches: yourDetails.height.inches ?? null,
		heightCm: yourDetails.height.cm ?? null,
		weightStone: yourDetails.weight.stone ?? null,
		weightPounds: yourDetails.weight.pounds ?? null,
		weightKg: yourDetails.weight.kg ?? null,
	};

	const schema = {
		properties: {
			heightFeet: { type: "number", minimum: 1, maximum: 10 },
			heightInches: { type: "number", minimum: 0, maximum: 11 },
			heightCm: { type: "number", minimum: 30, maximum: 333 },
			weightStone: { type: "number", minimum: 3, maximum: 30 },
			weightPounds: { type: "number", minimum: 0, maximum: 13 },
			weightKg: { type: "number", minimum: 2, maximum: 196 },
		},
	};

	const formSchema = {
		type: "object",
	};

	const [ethnicity, setEthnicity] = useState(yourDetails.ethnicity ?? "");

	//imperial = 0; metric = 1
	const [units, setUnits] = useState(yourDetails.units || 0);

	const [formData, setFormData] = useState(formFieldsDefault);

	const [boundaryErrors, setBoundaryErrors] = useState(0);

	const onSetImperialHeight = (e) => {
		const weightData = {
			weightStone: formData.weightStone,
			weightPounds: formData.weightPounds,
			weightKg: formData.weightKg,
		};

		const input = e.target;
		const value =
			e.target.value || e.target.value === "0"
				? parseInt(e.target.value ?? 0)
				: null;

		let tempFeet, tempInches;

		if (input.name === "heightFeet") {
			tempFeet = value;
			tempInches = formData.heightInches;
		} else if (input.name === "heightInches") {
			tempFeet = formData.heightFeet;
			tempInches = value;
		}

		if ((tempFeet === tempInches) === null) {
			setFormData({
				...weightData,
				heightFeet: null,
				heightInches: null,
				heightCm: null,
			});
			return;
		}

		const totalInches = (tempFeet ?? 0) * 12 + (tempInches ?? 0);

		const result = {
			...weightData,
			heightFeet: tempFeet,
			heightInches: tempInches,
			heightCm: parseInt((totalInches / 0.393700787).toFixed(0)),
		};

		setFormData(result);
	};

	const onSetMetricHeight = (e) => {
		const weightData = {
			weightStone: formData.weightStone,
			weightPounds: formData.weightPounds,
			weightKg: formData.weightKg,
		};
		if (e.target.value) {
			const tempInches =
				e.target.value || e.target.value === "0"
					? parseInt((parseInt(e.target.value ?? 0) * 0.393700787).toFixed(0))
					: null;

			const result = {
				...weightData,
				heightFeet: Math.floor(tempInches / 12),
				heightInches: tempInches % 12,
				heightCm: parseInt(e.target.value),
			};

			setFormData(result);
		} else {
			setFormData({
				...weightData,
				heightFeet: null,
				heightInches: null,
				heightCm: null,
			});
		}
	};

	const onSetImperialWeight = (e) => {
		const heightData = {
			heightFeet: formData.heightFeet,
			heightInches: formData.heightInches,
			heightCm: formData.heightCm,
		};

		const input = e.target;
		const value =
			e.target.value || e.target.value === "0"
				? parseInt(e.target.value ?? 0)
				: null;

		let tempStone, tempPounds;

		if (input.name === "weightStone") {
			tempStone = value;
			tempPounds = formData.weightPounds;
		} else if (input.name === "weightPounds") {
			tempStone = formData.weightStone;
			tempPounds = value;
		}

		if ((tempPounds === tempStone) === null) {
			setFormData({
				...heightData,
				weightStone: null,
				weightPounds: null,
				weightKg: null,
			});
			return;
		}

		const totalPounds = (tempStone ?? 0) * 14 + (tempPounds ?? 0);

		const result = {
			...heightData,
			weightStone: tempStone,
			weightPounds: tempPounds,
			weightKg: parseInt((totalPounds / 2.2046).toFixed(0)),
		};

		setFormData(result);
	};

	const onSetMetricWeight = (e) => {
		const heightData = {
			heightFeet: formData.heightFeet,
			heightInches: formData.heightInches,
			heightCm: formData.heightCm,
		};

		if (e.target.value) {
			const tempPounds =
				e.target.value || e.target.value === "0"
					? parseInt((parseInt(e.target.value ?? 0) * 2.2046).toFixed(0))
					: null;

			const result = {
				...heightData,
				weightStone: Math.floor(tempPounds / 14),
				weightPounds: tempPounds % 14,
				weightKg: parseInt(e.target.value),
			};
			setFormData(result);
		} else {
			setFormData({
				...heightData,
				weightStone: null,
				weightPounds: null,
				weightKg: null,
			});
		}
	};

	useEffect(() => {
		const warnings = [];
		const sp = schema.properties;
		let errors = 0;
		if (units === 0) {
			if (formData.heightFeet || formData.heightFeet === 0) {
				if (
					formData.heightFeet > sp.heightFeet.maximum ||
					(formData.heightFeet === sp.heightFeet.maximum &&
						formData.heightInches > 0)
				) {
					warnings.push(
						`Your height is higher than ${sp.heightFeet.maximum} feet`
					);
					errors++;
				} else if (formData.heightFeet < sp.heightFeet.minimum) {
					warnings.push(
						`Your height is lower than ${sp.heightFeet.minimum} feet`
					);
					errors++;
				}
			} else if (formData.heightInches || formData.heightInches === 0) {
				warnings.push(`Your height is only in inches`);
				errors++;
			}

			if (formData.heightInches > sp.heightInches.maximum) {
				warnings.push("Your inches unit is a foot or greater");
			}

			if (formData.weightStone || formData.weightStone === 0) {
				if (
					formData.weightStone > sp.weightStone.maximum ||
					(formData.weightStone === sp.weightStone.maximum &&
						formData.weightPounds > 0)
				) {
					warnings.push(
						`Your weight is higher than ${sp.weightStone.maximum} stone`
					);
					errors++;
				} else if (formData.weightStone < sp.weightStone.minimum) {
					warnings.push(
						`Your weight is lower than ${sp.weightStone.minimum} stone`
					);
					errors++;
				}
			} else if (formData.weightPounds || formData.weightPounds === 0) {
				warnings.push(`Your weight is only in pounds`);
				errors++;
			}

			if (formData.weightPounds > sp.weightPounds.maximum) {
				warnings.push("Your pounds unit is a stone or greater");
				errors++;
			}
		} else if (units === 1) {
			if (formData.heightCm || formData.heightCm === 0) {
				if (formData.heightCm > sp.heightCm.maximum) {
					warnings.push(`Your height is higher than ${sp.heightCm.maximum}cm`);
					errors++;
				} else if (formData.heightCm < sp.heightCm.minimum) {
					warnings.push(`Your height is lower than ${sp.heightCm.minimum}cm`);
					errors++;
				}
			}

			if (formData.weightKg || formData.weightKg === 0) {
				if (formData.weightKg > sp.weightKg.maximum) {
					warnings.push(`Your weight is higher than ${sp.weightKg.maximum}kg`);
					errors++;
				} else if (formData.weightKg < sp.weightKg.minimum) {
					warnings.push(`Your weight is lower than ${sp.weightKg.minimum}kg`);
					errors++;
				}
			}
		}

		setModalWarnings(warnings);
		setBoundaryErrors(errors);
	}, [formData]);

	let heightElements = null;
	let weightElements = null;

	if (units === 0) {
		heightElements = (
			<div className="flex">
				<Field
					key={"heightFeet"}
					type="number"
					className={"formField w-24 mr-2"}
					id={"heightFeet"}
					name="heightFeet"
					placeholder={"ft"}
					value={formData.heightFeet}
					onChange={onSetImperialHeight}
				/>
				<div className="relative top-3">ft</div>
				<Field
					key={"heightInches"}
					type="number"
					className={"formField w-24 ml-4 mr-2"}
					id={"heightInches"}
					name="heightInches"
					placeholder={"ins"}
					value={formData.heightInches}
					onChange={onSetImperialHeight}
				/>
				<div className="relative top-3">ins</div>
			</div>
		);

		weightElements = (
			<div className={"flex"}>
				<Field
					key={"weightStone"}
					type="number"
					className={"formField w-24 mr-2"}
					id={"weightStone"}
					name="weightStone"
					placeholder={"st"}
					value={formData.weightStone}
					onChange={onSetImperialWeight}
				/>
				<div className="relative top-3">st</div>
				<Field
					key={"weightPounds"}
					type="number"
					className={"formField w-24 ml-4 mr-2"}
					id={"weightPounds"}
					name="weightPounds"
					placeholder={"lbs"}
					value={formData.weightPounds}
					onChange={onSetImperialWeight}
				/>
				<div className="relative top-3">lbs</div>
			</div>
		);
	} else if (units === 1) {
		heightElements = (
			<div className={"flex"}>
				<Field
					key={"heightCm"}
					type="number"
					className={"formField w-24 mr-2"}
					id={"heightCm"}
					name="heightCm"
					placeholder={"cm"}
					value={formData.heightCm}
					onChange={onSetMetricHeight}
				/>
				<div className="relative top-3">cm</div>
			</div>
		);

		weightElements = (
			<div className={"flex"}>
				<Field
					key={"weightKg"}
					type="number"
					className={"formField w-24 mr-2"}
					id={"weightKg"}
					name="weightKg"
					placeholder={"kg"}
					value={formData.weightKg}
					onChange={onSetMetricWeight}
				/>
				<div className="relative top-3">kg</div>
			</div>
		);
	}

	const setYourDetailsState = () => {
		const convert = (value) => {
			if (value === 0 || value) {
				return parseInt(value, 10);
			} else {
				return null;
			}
		};

		yourDetails.ethnicity = ethnicity;
		yourDetails.height.feet = convert(formData.heightFeet);
		yourDetails.height.inches = convert(formData.heightInches);
		yourDetails.height.cm = convert(formData.heightCm);
		yourDetails.weight.stone = convert(formData.weightStone);
		yourDetails.weight.pounds = convert(formData.weightPounds);
		yourDetails.weight.kg = convert(formData.weightKg);
		yourDetails.units = units;

		const nullChecks = [];

		const addNullCheck = (value) => {
			nullChecks.push(value === null || value === "");
		};

		addNullCheck(ethnicity);
		addNullCheck(formData.heightFeet);
		addNullCheck(formData.heightCm);
		addNullCheck(formData.weightStone);
		addNullCheck(formData.weightKg);

		yourDetails.complete = nullChecks.every((e) => !e);
	};

	const hideNext = () => {};

	const [modalWarnings, setModalWarnings] = useState([]);

	const onFinish = () => {
		return new Promise((resolve, reject) => {
			if (userPid.mobilePhone === null || userPid.mobilePhone === "") {
				const yourControl = getUserSettings().yourControl;
				if (yourControl.reminders === null) {
				} else {
					yourControl.reminders = 0;
					saveYourControl(yourControl, false);
				}
			}

			setYourDetailsState();
			saveYourDetails(yourDetails)
				.then((yourDetailsResponse) => {
					if (yourDetailsResponse?.success === true) {
						saveUserPid(userPid)
							.then((pidResponse) => {
								if (pidResponse.success === true) {
									resolve([yourDetailsResponse, pidResponse]);
								}
							})
							.catch((error) => onSaveFailure(error));
					} else {
						resolve();
					}
				})
				.catch((error) => onSaveFailure(error));
		});
	};

	const [ethnicityOptions, setEthnicityOptions] = useState([]);

	useEffect(() => {
		let aborted = false;

		getList("ethnicity")
			.then((result) => {
				if (aborted) {
					return;
				}

				if (result.success) {
					const ret = [];
					for (const l of result.data.list) {
						ret.push({
							id: l.display,
							label: l.display,
							value: l.value,
						});
					}
					setEthnicityOptions(ret);
				}
			})
			.catch(() => {
				if (aborted) {
					return;
				}
			});

		return () => {
			aborted = true;
		};
	}, []);

	const initialEthnicity = ethnicityOptions.find(
		(eth) => eth.value === ethnicity
	);
	const [currentEthnicity, setCurrentEthnicity] = useState(initialEthnicity);
	useEffect(() => {
		if (currentEthnicity && currentEthnicity.value != null) {
			setEthnicity(currentEthnicity.value);
		} else if (ethnicityOptions.length) {
			const initialEthnicity = ethnicityOptions.find(
				(eth) => eth.value === ethnicity
			);

			if (initialEthnicity) {
				setEthnicity(initialEthnicity.value);
				setCurrentEthnicity(initialEthnicity);
			} else {
				setEthnicity("");
				setCurrentEthnicity();
			}
		}
	}, [currentEthnicity, ethnicityOptions, ethnicity]);

	const [modalOptions, setModalOptions] = useState({});

	useEffect(() => {
		setModalOptions({
			backText: "Cancel",
			nextText: "Confirm",
			content: modalWarnings.map((e) => <p>{e}</p>),
			enableModal: boundaryErrors > 0,
		});
	}, [boundaryErrors, modalWarnings]);

	return (
		<>
			<SetupDataPageTemplate
				headerDetails={headerDetails}
				backTo={`${pathRoot}/1`}
				nextTo={setupData}
				onClickBack={setYourDetailsState}
				onClickNext={{ func: onFinish, async: true }}
				nextTextOverride={"Finish"}
				hideNext={hideNext()}
				modalOptions={modalOptions}
			>
				{/* <h3 className={"text-xl mb-3"}>Extra Data:</h3> */}
				<Form schema={formSchema} data={formData}>
					<div className={"formInputContainer"}>
						<label className={"basicFormLabel"} htmlFor={"surname"}>
							Ethnicity
						</label>
						<div className="flex-grow relative">
							<Select
								options={ethnicityOptions}
								selectedOption={
									currentEthnicity || {
										id: "Please choose an option...",
										label: "Please choose an option...",
										value: "zz",
									}
								}
								handleChange={(value) => {
									setCurrentEthnicity(
										ethnicityOptions.find((eth) => eth.id === value)
									);
								}}
								bg={"bg-gray-50"}
							/>
						</div>
					</div>

					<div className={"formInputContainer mt-12"}>
						<label className={"basicFormLabel"}>Units</label>

						<div className={`radioSlider flex-1`}>
							<div className={"w-full"}>
								<Field
									type="radio"
									name="units"
									id="imperial"
									className={"hidden radioOption"}
									value={"imperial"}
									checked={units === 0}
									onClick={(w) => setUnits(0)}
								/>
								<label
									htmlFor={"imperial"}
									className={`option ${
										units === 0
											? `bg-black text-white font-medium`
											: "bg-gray-100"
									}`}
								>
									Imperial
								</label>
							</div>
							<div className={"w-full"}>
								<Field
									type="radio"
									name="units"
									id="metric"
									className={"hidden radioOption"}
									value={"metric"}
									checked={units === 1}
									onClick={(w) => setUnits(1)}
								/>
								<label
									htmlFor={"metric"}
									className={`option ${
										units === 1
											? `bg-black text-white font-medium`
											: "bg-gray-100"
									}`}
								>
									Metric
								</label>
							</div>
						</div>
					</div>

					<div className={"formInputContainer mt-6"}>
						<div>
							<label className={"basicFormLabel"}>Height</label>
							<div className={"flex-1 flex flex-row"}>{heightElements}</div>
						</div>
					</div>

					<div className={"formInputContainer"}>
						<label className={"basicFormLabel"}>Weight</label>
						<div className={"flex-1 flex flex-row"}>{weightElements}</div>
					</div>
				</Form>
			</SetupDataPageTemplate>
		</>
	);
}
